import {
  ThreadContext,
  GlobalPostViewModel,
} from 'functions/app/thread/view-global'
import { PostViewModel } from 'functions/app/thread/view-latest'
import { pages } from '@app/util/links'
import { initiativeTabs } from '@app/pages/discover/initiatives/sections/initiativeTabs'
import { plotTabs } from '@app/pages/discover/plots/sections/plotTabs'
import { housingFormTabs } from '@app/pages/discover/housing-forms/constants'
import {
  municipalityTabs,
  regionTabs,
} from '@app/pages/discover/regions/constants'
import { topicCommunityTabs } from '@app/pages/discover/topic-communities/constants'

// Collect the first occurrence of each entity type across all threads in a
// post. A single post can have multiple thread links (e.g. municipality thread
// + region thread created automatically by the backend).
export type CollectedEntities = {
  initiative?: { id?: number; title?: string; slug?: string }
  plot?: { id?: number; name?: string; slug?: string }
  housingForm?: { id?: number; title?: string; slug?: string }
  municipality?: { id?: number; name?: string; slug?: string }
  region?: { id?: number; name?: string; slug?: string }
  topicCommunity?: { id?: number; name?: string; slug?: string }
}

export const collectEntities = (threads?: ThreadContext[]): CollectedEntities => {
  const result: CollectedEntities = {}
  for (const thread of threads ?? []) {
    if (!result.initiative && thread.initiative?.id) {
      result.initiative = thread.initiative
    }
    if (!result.plot && thread.plot?.id) {
      result.plot = thread.plot
    }
    if (!result.housingForm && thread.housingForm?.id) {
      result.housingForm = thread.housingForm
    }
    if (!result.municipality && thread.municipality?.id) {
      result.municipality = thread.municipality
    }
    if (!result.region && thread.region?.id) {
      result.region = thread.region
    }
    if (!result.topicCommunity && thread.topicCommunity?.id) {
      result.topicCommunity = thread.topicCommunity
    }
  }
  return result
}

export type PostedInLink = {
  label: string
  link?: string
}

// Priority: initiative > plot > housingForm > municipality > region > topicCommunity.
// When both municipality and region are present, return two separate links.
export const getPostedInLinks = (
  threads?: ThreadContext[],
): PostedInLink[] => {
  const { initiative, plot, housingForm, municipality, region, topicCommunity } =
    collectEntities(threads)

  if (initiative?.title) {
    return [
      {
        label: initiative.title,
        link: initiative.slug
          ? `${pages.initiative(initiative.slug)}?tab=${initiativeTabs.discussion}`
          : undefined,
      },
    ]
  }
  if (plot?.name) {
    return [
      {
        label: plot.name,
        link: plot.slug
          ? `${pages.plot(plot.slug)}?tab=${plotTabs.discussion}`
          : undefined,
      },
    ]
  }
  if (housingForm?.title) {
    return [
      {
        label: housingForm.title,
        link: housingForm.slug
          ? `${pages.housingForm(housingForm.slug)}?tab=${housingFormTabs.discussion}`
          : undefined,
      },
    ]
  }
  if (municipality?.name) {
    const links: PostedInLink[] = [
      {
        label: municipality.name,
        link: municipality.slug
          ? `${pages.municipalityPage(municipality.slug)}?tab=${municipalityTabs.discussion}`
          : undefined,
      },
    ]
    if (region?.name) {
      links.push({
        label: region.name,
        link: region.slug
          ? `${pages.regionPage(region.slug)}?tab=${regionTabs.discussion}`
          : undefined,
      })
    }
    return links
  }
  if (region?.name) {
    return [
      {
        label: region.name,
        link: region.slug
          ? `${pages.regionPage(region.slug)}?tab=${regionTabs.discussion}`
          : undefined,
      },
    ]
  }
  if (topicCommunity?.name) {
    return [
      {
        label: topicCommunity.name,
        link: topicCommunity.slug
          ? `${pages.topicCommunity(topicCommunity.slug)}?tab=${topicCommunityTabs.discussion}`
          : undefined,
      },
    ]
  }
  return []
}

export const getPostedInLabel = (
  threads?: ThreadContext[],
): string | undefined => {
  const links = getPostedInLinks(threads)
  if (links.length === 0) {
    return undefined
  }
  return links.map((link) => link.label).join(', ')
}

// Returns the primary entity link (municipality when both are present).
export const getPostLink = (
  threads?: ThreadContext[],
): string | undefined => {
  return getPostedInLinks(threads)[0]?.link
}

export const getPostShareUrl = (postId: number): string => {
  const path = `${pages.discussions}?post=${postId}`
  if (typeof window === 'undefined') {
    return path
  }
  return `${window.location.origin}${path}`
}

export const getPostShareTitle = (
  title?: string,
  content?: string,
): string | undefined => {
  const trimmedTitle = title?.trim()
  if (trimmedTitle) {
    return trimmedTitle
  }
  const trimmedContent = content?.trim()
  if (trimmedContent) {
    return trimmedContent.slice(0, 100)
  }
  return undefined
}

export const toGlobalPostViewModel = (
  post: PostViewModel,
): GlobalPostViewModel => ({
  id: post.id,
  title: post.title ?? undefined,
  content: post.content,
  createdAt: post.createdAt,
  updatedAt: post.updatedAt,
  visibility: undefined,
  author: post.author,
  categories: post.categories ?? [],
  media: post.media ?? [],
  threads: post.threads ?? [],
  viewers: post.viewers ?? [],
})
